import React, { useState, useEffect } from 'react';
import { API, getToken } from './adminApi';

const CommunityMembersTab = () => {
  const [communities, setCommunities] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [communityTitle, setCommunityTitle] = useState('');
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'removeFromCommunity' | 'deleteAccount', userIds, label }
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [restoreError, setRestoreError] = useState('');
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);

  useEffect(() => {
    fetch(`${API}/communities`)
      .then(res => res.json())
      .then(data => setCommunities(Array.isArray(data)
        ? [...data].sort((a, b) => (a.title || '').localeCompare(b.title || ''))
        : []))
      .catch(() => setCommunities([]));
    fetchDeletedUsers();
  }, []);

  const fetchDeletedUsers = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/deleted-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setDeletedUsers(data.deletedUsers || []);
    } catch (err) {
      console.error('[fetchDeletedUsers] failed:', err.message);
    }
  };

  const restoreDeletedUser = async (id) => {
    setRestoringId(id);
    setRestoreError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/deleted-users/${id}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await fetchDeletedUsers();
      if (selectedId) await fetchMembers(selectedId);
    } catch (err) {
      setRestoreError(err.message);
    } finally {
      setRestoringId(null);
    }
  };

  const fetchMembers = async (communityId) => {
    if (!communityId) return;
    setLoading(true);
    setFetchError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/communities/${communityId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCommunityTitle(data.communityTitle);
      setMembers(data.members || []);
    } catch (err) {
      setFetchError(err.message);
      setMembers(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (e) => {
    const communityId = e.target.value;
    setSelectedId(communityId);
    setMembers(null);
    setActionError('');
    setSelectedIds(new Set());
    fetchMembers(communityId);
  };

  const toggleSelected = (userId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!members) return;
    setSelectedIds(prev => (prev.size === members.length ? new Set() : new Set(members.map(m => m.userId))));
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    setActionError('');
    try {
      const token = await getToken();
      const results = await Promise.all(confirmAction.userIds.map(async (userId) => {
        const url = confirmAction.type === 'deleteAccount'
          ? `${API}/admin/users/${userId}`
          : `${API}/admin/communities/${selectedId}/members/${userId}`;
        const res = await fetch(url, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        return { userId, ok: res.ok, error: data.error };
      }));

      const failed = results.filter(r => !r.ok);
      if (failed.length > 0) {
        throw new Error(`${failed.length} of ${results.length} failed: ${failed.map(f => f.error).join('; ')}`);
      }

      setConfirmAction(null);
      setSelectedIds(new Set());
      await fetchMembers(selectedId);
      if (confirmAction.type === 'deleteAccount') await fetchDeletedUsers();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const daysLeft = (restoreDeadline) => {
    const ms = new Date(restoreDeadline).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
  };

  return (
    <div className="as-community-members">
      {restoreError && <div className="as-error">{restoreError}</div>}
      {deletedUsers.length > 0 && (
        <div className="as-trash-panel">
          <h2 className="as-section-title">🗑️ Recently Deleted ({deletedUsers.length})</h2>
          <div className="as-trash-list">
            {deletedUsers.map(d => (
              <div key={d._id} className="as-trash-card">
                {d.photo ? (
                  <img
                    src={d.photo}
                    alt=""
                    className="as-trash-img as-signup-img--clickable"
                    onClick={() => setEnlargedPhoto(d.photo)}
                  />
                ) : (
                  <div className="as-trash-img-placeholder">?</div>
                )}
                <div className="as-trash-details">
                  <strong>{d.firstName || '(no name)'}</strong>
                  <div className="as-card-meta">
                    {d.communityTitles.join(', ') || 'no communities'} · deleted by {d.deletedByAdminEmail}
                  </div>
                  <div className="as-card-meta">{daysLeft(d.restoreDeadline)} day{daysLeft(d.restoreDeadline) === 1 ? '' : 's'} left to restore</div>
                </div>
                <button
                  className="as-filter-tab"
                  onClick={() => restoreDeletedUser(d._id)}
                  disabled={restoringId === d._id}
                >
                  {restoringId === d._id ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="as-activity-toolbar">
        <select className="as-filter-tab" value={selectedId} onChange={handleSelect}>
          <option value="">Select a community…</option>
          {communities.map(c => (
            <option key={c._id} value={c._id}>{c.title}</option>
          ))}
        </select>
        {selectedId && (
          <button className="as-filter-tab" onClick={() => fetchMembers(selectedId)}>↻ Refresh</button>
        )}
      </div>

      {fetchError && <div className="as-error">{fetchError}</div>}
      {actionError && <div className="as-error">{actionError}</div>}
      {loading && <div className="as-loading">Loading…</div>}

      {members && (
        <>
          <h2 className="as-section-title">{communityTitle} — Members ({members.length})</h2>
          {members.length === 0 && <div className="as-empty">No members in this community.</div>}

          {members.length > 0 && (
            <div className="as-bulk-bar">
              <label className="as-bulk-selectall">
                <input
                  type="checkbox"
                  checked={selectedIds.size === members.length}
                  onChange={toggleSelectAll}
                />
                Select all
              </label>
              {selectedIds.size > 0 && (
                <>
                  <span className="as-bulk-count">{selectedIds.size} selected</span>
                  <button
                    className="as-filter-tab"
                    onClick={() => setConfirmAction({
                      type: 'removeFromCommunity',
                      userIds: [...selectedIds],
                      label: `${selectedIds.size} selected member${selectedIds.size === 1 ? '' : 's'}`,
                    })}
                  >
                    Remove selected from community
                  </button>
                  <button
                    className="as-filter-tab as-filter-tab--danger"
                    onClick={() => setConfirmAction({
                      type: 'deleteAccount',
                      userIds: [...selectedIds],
                      label: `${selectedIds.size} selected member${selectedIds.size === 1 ? '' : 's'}`,
                    })}
                  >
                    Delete selected accounts
                  </button>
                </>
              )}
            </div>
          )}

          <div className="as-signup-list">
            {members.map(m => (
              <div key={m.userId} className={`as-signup-card${selectedIds.has(m.userId) ? ' as-signup-card--selected' : ''}`}>
                <input
                  type="checkbox"
                  className="as-signup-checkbox"
                  checked={selectedIds.has(m.userId)}
                  onChange={() => toggleSelected(m.userId)}
                />
                <div className="as-signup-photo-gallery">
                  {m.pics && m.pics.length > 0 ? (
                    m.pics.map((pic, i) => (
                      <img
                        key={i}
                        src={pic}
                        alt=""
                        className="as-signup-img as-signup-img--clickable"
                        onClick={() => setEnlargedPhoto(pic)}
                      />
                    ))
                  ) : (
                    <div className="as-signup-img-placeholder">?</div>
                  )}
                </div>
                <div className="as-signup-details">
                  <div className="as-signup-headline">
                    <strong>{m.firstName || '(no name)'}</strong>
                    {m.age != null && <span className="as-signup-age">{m.age}</span>}
                  </div>
                  <div className="as-card-meta">
                    {m.localArea || 'Location not set'}
                    {!m.mainProfileFound && ' · ⚠ no main profile found (orphaned community record)'}
                  </div>
                  <div className="as-card-meta as-card-meta--id">ID: {m.userId}</div>

                  {m.communityProfile ? (
                    <div className="as-signup-profile-fields">
                      {Object.entries(m.communityProfile).map(([field, value]) => {
                        if (value === '' || value == null || (Array.isArray(value) && value.length === 0)) return null;
                        return (
                          <div key={field} className="as-signup-profile-field">
                            <span className="as-signup-field-name">{field}:</span>{' '}
                            <span>{Array.isArray(value) ? value.join(', ') : String(value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="as-signup-profile-field as-signup-profile-field--empty">No community profile filled in yet</div>
                  )}

                  <div className="as-signup-community as-signup-community--empty">
                    Also in: {m.joinedCommunities.filter(jc => jc.communityId !== selectedId).map(jc => jc.communityTitle).filter(Boolean).join(', ') || 'no other communities'}
                  </div>

                  <div className="as-member-actions">
                    <button
                      className="as-filter-tab"
                      onClick={() => setConfirmAction({ type: 'removeFromCommunity', userIds: [m.userId], label: m.firstName || 'this user' })}
                    >
                      Remove from community
                    </button>
                    <button
                      className="as-filter-tab as-filter-tab--danger"
                      onClick={() => setConfirmAction({ type: 'deleteAccount', userIds: [m.userId], label: m.firstName || 'this user' })}
                    >
                      Delete entire account
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {confirmAction && (
        <div className="as-confirm-overlay" onClick={() => !actionLoading && setConfirmAction(null)}>
          <div className="as-confirm-modal" onClick={(e) => e.stopPropagation()}>
            {confirmAction.type === 'deleteAccount' ? (
              <>
                <h3>Delete {confirmAction.label}'s entire account{confirmAction.userIds.length > 1 ? 's' : ''}?</h3>
                <p>This removes their account, every community they've joined, and their profile data. It's recoverable for 7 days from the "Recently Deleted" list below — after that it's permanent.</p>
              </>
            ) : (
              <>
                <h3>Remove {confirmAction.label} from {communityTitle}?</h3>
                <p>This deletes their profile for this community only. Their account and other communities are untouched.</p>
              </>
            )}
            <div className="as-confirm-buttons">
              <button className="as-filter-tab" onClick={() => setConfirmAction(null)} disabled={actionLoading}>Cancel</button>
              <button className="as-filter-tab as-filter-tab--danger" onClick={runConfirmedAction} disabled={actionLoading}>
                {actionLoading ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {enlargedPhoto && (
        <div className="as-photo-overlay" onClick={() => setEnlargedPhoto(null)}>
          <img src={enlargedPhoto} alt="" className="as-photo-overlay-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default CommunityMembersTab;
